import { create } from 'zustand';
import { micConstraint, applySpeaker, applySpeakerVolume, useAudioDevices } from './audioDevices';
import { suppressNoise, type SuppressedStream } from '../audio/noiseSuppressor';
import { boostMic } from '../audio/micGain';
import { gateStream } from '../audio/noiseGate';
import { acquireSipLock, holdsSipLock, sipLockName, waitForSipLock } from './sipTabLock';
import { CLAIM_RENEW_MS, claimSipLine, otherDeviceMessage, type OtherDevice } from './sipDeviceClaim';
// JsSIP is loaded dynamically so the bundle stays light and tests can mock it.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let JsSIP: any | null = null;

// The RNNoise graph for the in-flight call. Kept module-level (like the active
// session) so call teardown can dispose it regardless of which handler fires.
let activeSuppressed: SuppressedStream | null = null;

/**
 * Build the outbound mic stream for a call, applying RNNoise background-noise
 * suppression when the agent has it enabled. Always resolves to a usable
 * MediaStream — on any failure it returns the raw mic so the call still goes
 * through. The returned stream is what JsSIP sends over RTP.
 */
async function buildOutboundStream(): Promise<MediaStream> {
  // Tear down any leftover graph from a prior call before starting a new one.
  disposeSuppressed();
  const wantRnnoise = useAudioDevices.getState().noiseSuppression;

  // Capture the mic. When RNNoise will run we ask the browser to DISABLE its
  // own noiseSuppression — stacking the browser denoiser on top of RNNoise
  // double-processes the signal and muddies the agent's voice. We keep
  // echoCancellation + AGC either way. When RNNoise is off, we lean on the
  // browser's built-in noiseSuppression instead.
  const raw = await navigator.mediaDevices.getUserMedia({
    audio: micConstraint(!wantRnnoise),
    video: false,
  });

  if (!wantRnnoise) {
    return finalizeOutbound({
      stream: raw,
      active: false,
      dispose: () => raw.getTracks().forEach((t) => t.stop()),
    });
  }

  const suppressed = await suppressNoise(raw);
  if (!suppressed.active) {
    // RNNoise failed to start — we captured the mic WITHOUT the browser
    // denoiser, so re-acquire with the built-in noiseSuppression on rather than
    // sending a fully unfiltered mic. (suppressNoise already stopped `raw`.)
    try {
      const fallback = await navigator.mediaDevices.getUserMedia({
        audio: micConstraint(true),
        video: false,
      });
      return finalizeOutbound({
        stream: fallback,
        active: false,
        dispose: () => fallback.getTracks().forEach((t) => t.stop()),
      });
    } catch {
      return finalizeOutbound(suppressed);
    }
  }

  return finalizeOutbound(suppressed);
}

/**
 * Final stage of the outbound pipeline: apply the agent's tuning on top of
 * whatever the branch produced (raw / RNNoise / fallback) —
 *   1. noise gate (blocks nearby voices while the agent is silent), then
 *   2. mic gain (the fix for a too-quiet mic)
 * — and register the composed graph for teardown. Defaults (gate off,
 * gain 100%) add no extra graph. On any WebAudio failure the previous stage's
 * stream is used — a call must never fail because of a tuning knob.
 */
function finalizeOutbound(base: SuppressedStream): MediaStream {
  const { micGain, gateEnabled, gateThreshold } = useAudioDevices.getState();
  let current: SuppressedStream = base;

  if (gateEnabled) {
    try {
      const gated = gateStream(current.stream, gateThreshold ?? 35);
      const prev = current;
      current = {
        stream: gated.stream,
        active: prev.active,
        dispose: () => {
          gated.dispose();
          prev.dispose();
        },
      };
    } catch {
      /* gate failed — keep the ungated stream */
    }
  }

  const gainPct = micGain ?? 100;
  if (gainPct !== 100) {
    try {
      const boosted = boostMic(current.stream, gainPct);
      const prev = current;
      current = {
        stream: boosted.stream,
        active: prev.active,
        dispose: () => {
          boosted.dispose();
          prev.dispose();
        },
      };
    } catch {
      /* boost failed — keep the unboosted stream */
    }
  }

  activeSuppressed = current;
  return current.stream;
}

// Presence keep-alive: while the softphone is SIP-registered, ping the server
// every 60s so the stale-sweep (which flips rows older than 90s to offline)
// never downs a live agent. The ping only touches/lifts-from-offline — it
// never overrides a manually chosen status (break/lunch/acw).
let presenceHeartbeatTimer: ReturnType<typeof setInterval> | null = null;

function startPresenceHeartbeat(): void {
  if (presenceHeartbeatTimer) return;
  const ping = async () => {
    try {
      const { apiClient } = await import('@/shared/api/client');
      await apiClient.post('/agent/settings/heartbeat');
    } catch {
      /* transient network error — next tick will retry */
    }
  };
  void ping();
  presenceHeartbeatTimer = setInterval(() => void ping(), 60_000);
}

function stopPresenceHeartbeat(): void {
  if (presenceHeartbeatTimer) {
    clearInterval(presenceHeartbeatTimer);
    presenceHeartbeatTimer = null;
  }
}

/** Stop the RNNoise graph + mic tracks for the current call, if any. */
function disposeSuppressed(): void {
  try {
    activeSuppressed?.dispose();
  } catch {
    /* already disposed */
  }
  activeSuppressed = null;
}

/**
 * הודעה קריאה לכשל תפיסת המיקרופון. שמות החריגות מגיעים מ-getUserMedia
 * (NotAllowedError כשההרשאה נדחתה, NotFoundError כשאין מכשיר, וכו').
 */
export function micErrorReason(e?: { name?: string; message?: string } | null): string {
  switch (e?.name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return 'הגישה למיקרופון נחסמה בדפדפן. לחץ על סמל המנעול שליד כתובת האתר, אפשר "מיקרופון", ורענן את הדף.';
    case 'NotFoundError':
    case 'OverconstrainedError':
      return 'לא נמצא מיקרופון מחובר. חבר מיקרופון או אוזניות ובחר אותו בהגדרות השמע.';
    case 'NotReadableError':
      return 'המיקרופון תפוס על ידי תוכנה אחרת (זום, טימס, הקלטה). סגור אותה ונסה שוב.';
    default:
      return `לא ניתן לגשת למיקרופון${e?.name ? ` (${e.name})` : ''}. בדוק את הרשאות המיקרופון בדפדפן ונסה שוב.`;
  }
}

export type SipState =
  | 'idle' | 'registering' | 'ready'
  | 'ringing-in' | 'ringing-out' | 'on-call' | 'on-hold' | 'wrap-up' | 'error';

interface IncomingCall {
  from: string;
  displayName?: string;
}

interface ActiveCall {
  callId?: number;
  number: string;
  direction: 'inbound' | 'outbound';
  startedAt: number;
  durationSec: number;
  muted: boolean;
  onHold: boolean;
  /**
   * מי סיים את השיחה — נקבע ברגע הניתוק ונשאר לאורך ה-wrap-up. 'remote' =
   * הצד השני (בשיחת קמפיין: הלקוח ניתק), 'local' = הנציג לחץ "נתק".
   */
  endedBy?: 'local' | 'remote';
}

/**
 * כמה מחכים לאיסוף מועמדי ICE לפני שעונים. JsSIP ממתין כברירת מחדל לסיום
 * האיסוף המלא — עם שרתי STUN/TURN זה יכול להימשך שניות, והלקוח (שכבר ענה
 * בקמפיין) שומע מוזיקה בזמן הזה (06/10/2026: "מתחברת רק אחרי 3-4 שניות").
 * relay מגיע ⇒ מוכנים מיד; srflx ⇒ עוד חלון קצר ל-relay; ותקרה כוללת.
 */
export const ICE_READY = { afterSrflxMs: 400, maxMs: 1200 };

export interface SipDid {
  number: string;
  label?: string | null;
}

export interface SipCredentials {
  wsServer: string;
  uri: string;
  password: string;
  displayName?: string;
  domain?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  iceServers?: any[];
  defaultCallerDid?: string | null;
  dids?: SipDid[];
}

interface SipStore {
  state: SipState;
  registered: boolean;
  /** למשתמש אין שלוחה בכלל (מנהל בלי קו) — מצב תקין, לא שגיאה. */
  noLine: boolean;
  incoming: IncomingCall | null;
  active: ActiveCall | null;
  error: string | null;
  /**
   * כשל מיקרופון בניסיון החיוג האחרון. נפרד מ-`error` כי הוא חוסם: כל עוד
   * הוא מלא ה-UI מציג חלונית ולא מאפשר לחייג, שאחרת הלחיצה "מצליחה"
   * לכאורה ואף INVITE לא יוצא.
   */
  micError: string | null;
  creds: SipCredentials | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ua: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  session: any;

  recordCalls: boolean;
  setRecordCalls: (v: boolean) => void;

  register: (creds?: SipCredentials | null) => Promise<void>;
  unregister: () => void;
  /** "מנותק" אמיתי: הקו לא רשום ו-useSipRegistration לא ירשום אותו מחדש. */
  lineDisabled: boolean;
  /**
   * הקו רשום בלשונית אחרת בדפדפן הזה (sipTabLock). הלשונית הזו לא נרשמת —
   * אחרת השתיים מעיפות זו את זו והשיחה נענית במקום שהנציג לא מקשיב בו.
   */
  otherTab: boolean;
  /** הקו מוחזק במחשב/דפדפן אחר של אותו משתמש (sipDeviceClaim). */
  otherDevice: OtherDevice | null;
  /** מעביר את הקו ללשונית הזו (גונב את המנעול מהלשונית האחרת ונרשם). */
  takeOverLine: () => Promise<boolean>;
  setPresence: (status: string) => Promise<void>;
  /**
   * מונה שכל עלייה שלו מבקשת מ-useSipRegistration למשוך קרדנצ'לס ולרשום
   * מחדש — גם כשה-hook כבר "סיים" (הוא רץ מחדש רק כש-registered נופל, ולכן
   * רישום שנכשל מלכתחילה נשאר תקוע עד רענון).
   */
  reconnectNonce: number;
  /**
   * ניסיון חיבור מחדש של הקו: עם creds שמורים — רישום מחדש; בלעדיהם —
   * בקשה ל-hook למשוך אותם. מחזיר האם הקו רשום בסוף ההמתנה.
   */
  reconnect: () => Promise<boolean>;
  /**
   * משיכת creds מחדש ורישום — אחרי שהשרת הקצה שלוחה חדשה (הצטרפות לקמפיין).
   * reconnect() לא מספיק: הוא נשען על creds שמורים, ובמצב noLine מוותר מיד.
   * מחזיר האם הקו רשום בסוף ההמתנה.
   */
  refreshLine: () => Promise<boolean>;
  dial: (number: string, callerId?: string, afterReconnect?: boolean) => Promise<void>;
  answer: () => Promise<void>;
  reject: () => void;
  hangup: () => void;
  hold: () => void;
  unhold: () => void;
  mute: (next?: boolean) => void;
  sendDtmf: (digit: string) => void;
  enterWrapup: () => void;
  finishWrapup: () => void;
  /** סגירת חלונית כשל המיקרופון (אחרי שהמשתמש תיקן את ההרשאה). */
  clearMicError: () => void;
}

let tickHandle: number | undefined;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let activeJsSipSession: any = null;

// Soft mode is an explicit opt-in (demo / tests with no SIP backend). Default
// is REAL whenever credentials are supplied to register().
const SOFT_MODE = import.meta.env.VITE_SIP_SOFT === '1';

const DEFAULT_DOMAIN = 'agent.kailai.tech';

/**
 * כמה זמן dial() מוכן לחכות לרישום מחדש לפני שהוא מוותר. ה-WebSocket
 * והרישום לוקחים בדרך כלל פחות משנייה; שש שניות מכסות גם שרת עמוס בלי
 * שהנציג ירגיש שהלחיצה "נבלעה". ניתן לקיצור בבדיקות.
 */
export const reconnectTiming = { waitMs: 6000, pollMs: 100 };

async function waitForRegistered(get: () => SipStore): Promise<boolean> {
  const deadline = Date.now() + reconnectTiming.waitMs;
  while (Date.now() < deadline) {
    const st = get();
    if (st.registered) return true;
    // כשל רישום מפורש (סיסמה שגויה, שרת דחה) — אין טעם להמשיך לחכות.
    if (st.state === 'error' && st.error && st.error !== RECONNECTING_MSG) return false;
    // השרת ענה שאין שלוחה (422 ⇒ register(null)) — אין למה לחכות.
    if (st.noLine && st.state === 'idle') return false;
    await new Promise((r) => setTimeout(r, reconnectTiming.pollMs));
  }
  return get().registered;
}

const OTHER_TAB_MSG = 'הקו פעיל בלשונית GoCall אחרת בדפדפן הזה. שיחות נכנסות ייענו שם — עבוד משם, או לחץ "העבר לכאן".';
const TAKEN_MSG = 'הקו עבר ללשונית GoCall אחרת. לחץ "העבר לכאן" כדי לעבוד מהלשונית הזו.';

const IN_CALL_STATES: SipState[] = ['on-call', 'on-hold', 'ringing-in', 'ringing-out'];

/**
 * יורדים מהקו כי הוא עבר למקום אחר — אבל לא חותכים שיחה חיה: יורדים ברגע
 * שהיא נגמרת. אחר כך `after` (המתנה שהקו יתפנה).
 */
function stepOffLine(patch: Partial<SipStore>, after: () => void): void {
  const release = () => {
    const st = useSipStore.getState();
    try { st.ua?.stop?.(); } catch { /* כבר עצור */ }
    stopPresenceHeartbeat();
    stopClaimRenewal();
    useSipStore.setState({ ua: null, registered: false, state: 'error', ...patch });
    after();
  };
  if (IN_CALL_STATES.includes(useSipStore.getState().state)) {
    // לא חותכים שיחה חיה — יורדים מהקו ברגע שהיא נגמרת.
    const unsub = useSipStore.subscribe((s) => {
      if (!IN_CALL_STATES.includes(s.state)) { unsub(); release(); }
    });
    return;
  }
  release();
}

/** לשונית אחרת גנבה את הקו: יורדים ממנו וממתינים בתור. */
function lineTakenElsewhere(): void {
  stepOffLine({ otherTab: true, error: TAKEN_MSG }, parkUntilLineFree);
}

/** מכשיר אחר לקח את הקו (השרת דחה את חידוש התביעה). */
function lineMovedToDevice(other: OtherDevice): void {
  stepOffLine({ otherDevice: other, error: otherDeviceMessage(other) }, pollUntilDeviceFree);
}

// חידוש התביעה בשרת כל עוד הקו רשום כאן.
let claimRenewTimer: ReturnType<typeof setInterval> | null = null;

function startClaimRenewal(): void {
  if (claimRenewTimer) return;
  claimRenewTimer = setInterval(() => {
    void claimSipLine(false).then((r) => {
      if (!r.owner && useSipStore.getState().registered) lineMovedToDevice(r.other);
    });
  }, CLAIM_RENEW_MS);
}

function stopClaimRenewal(): void {
  if (claimRenewTimer) { clearInterval(claimRenewTimer); claimRenewTimer = null; }
}

// מכשיר אחר מחזיק: בודקים כל 30ש אם שחרר/נרדם, ואז נרשמים כאן לבד.
let devicePollTimer: ReturnType<typeof setInterval> | null = null;

function stopDevicePoll(): void {
  if (devicePollTimer) { clearInterval(devicePollTimer); devicePollTimer = null; }
}

function pollUntilDeviceFree(): void {
  stopDevicePoll();
  devicePollTimer = setInterval(() => {
    const st = useSipStore.getState();
    if (!st.otherDevice || st.lineDisabled) { stopDevicePoll(); return; }
    void claimSipLine(false).then((r) => {
      const cur = useSipStore.getState();
      if (!r.owner || !cur.otherDevice) return;
      stopDevicePoll();
      useSipStore.setState({ otherDevice: null });
      if (cur.creds && !cur.lineDisabled) void cur.register(cur.creds);
    });
  }, CLAIM_RENEW_MS);
}

/**
 * "הכניסה האחרונה מנצחת" (30/09/2026, בקשת דורון): הרישום הראשון בטעינת הדף
 * לוקח את הקו בכוח, והמכשיר הקודם מתנתק לבד בחידוש הבא שלו. אחרי זה — רק
 * תביעה רגילה: ניתוק רגעי ברשת במכשיר הישן לא יחטוף את הקו בחזרה (פינג-פונג),
 * ומכשיר שהודח חוזר רק בפעולה מפורשת ("העבר לכאן" / סבב).
 */
let claimedThisPage = false;

/** לטסטים בלבד. */
export function __resetLineClaimForTests(): void {
  claimedThisPage = false;
}

/** ממתין שהלשונית המחזיקה תיסגר, ואז נרשם כאן לבד. */
function parkUntilLineFree(): void {
  const creds = useSipStore.getState().creds;
  if (!creds?.uri) return;
  waitForSipLock(sipLockName(creds.uri), {
    onGranted: () => {
      const st = useSipStore.getState();
      useSipStore.setState({ otherTab: false });
      // העברה בין לשוניות אינה "כניסה חדשה" — לא חוטפים ממכשיר אחר.
      claimedThisPage = true;
      if (st.creds && !st.lineDisabled) void st.register(st.creds);
    },
    onLost: lineTakenElsewhere,
  });
}

const NO_LINE_MSG = 'לא הוקצתה שלוחה למשתמש זה — חיוג מהדפדפן יתאפשר אחרי שמנהל יקצה שלוחה בהגדרות הנציגים.';
const RECONNECTING_MSG = 'הקו לא מחובר — מנסה להתחבר מחדש…';
const LINE_DOWN_MSG = 'הקו לא מחובר (SIP). ניסיון החיבור מחדש נכשל — בדוק את החיבור לאינטרנט ולחץ "התחבר מחדש", או רענן את הדף.';

// Call recording is ALWAYS on for the call center — by policy every agent call
// is recorded. The dialplan starts MixMonitor when the INVITE carries
// X-Record-Call: yes, so this flag is hard-pinned to true and the agent has no
// off-switch. (Whether recording happens at all is governed centrally by the
// admin recording settings, not by the softphone.)
function loadRecordPref(): boolean {
  return true;
}

// Default ICE servers when the backend doesn't supply any (mirrors KaliDialer).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function defaultIceServers(domain: string): any[] {
  return [
    { urls: `stun:${domain}:3478` },
    { urls: 'stun:stun.l.google.com:19302' },
  ];
}

function cleanCallerNumber(raw?: string): string {
  if (!raw) return '?';
  let num = String(raw);
  if (num.startsWith('sip:')) num = num.replace(/^sip:/, '').replace(/@.*$/, '');
  num = num.replace(/^\+/, '');
  if (num.startsWith('972') && num.length > 9) num = '0' + num.slice(3);
  return num || '?';
}

/**
 * מחזיר הודעת שגיאה אם המספר אינו חייג, או null אם הוא תקין.
 *
 * שלוחה פנימית (3–4 ספרות) מותרת — היא נפתרת בדיאלפלן ולא יוצאת ל-PSTN.
 * מעבר לכך נדרש מספר ישראלי שלם: 10 ספרות ב-0, או 972 בפורמט בינלאומי.
 * הבדיקה הזו תופסת בדיוק את המקרה של ספרה חסרה (למשל 050229831).
 */
export function invalidNumberReason(raw: string): string | null {
  const digits = (raw ?? '').replace(/\D/g, '');

  if (!digits) return 'לא הוזן מספר לחיוג';
  if (digits.length <= 4) return null;                    // שלוחה פנימית

  if (digits.startsWith('972')) {
    return digits.length === 12 ? null : `המספר ${raw} אינו תקין — מספר בינלאומי דורש 972 ועוד תשע ספרות`;
  }
  if (digits.startsWith('0')) {
    // קווי (02/03/04/08/09) וסלולרי (05x) כאחד הם 10 ספרות; 1-800 ודומיו ארוכים יותר.
    if (digits.startsWith('1')) return null;
    return digits.length === 10 ? null : `המספר ${raw} אינו תקין — נדרשות עשר ספרות (הוזנו ${digits.length})`;
  }
  if (digits.startsWith('1')) return null;                // 1-800 / *כוכביות

  return `המספר ${raw} אינו תקין — מספר ישראלי מתחיל ב-0 או ב-972`;
}

function buildTargetUri(phoneNumber: string, domain: string): string {
  const digits = String(phoneNumber).replace(/[^0-9*#]/g, '');
  return `sip:${digits}@${domain}`;
}

// Attach a remote RTCPeerConnection's inbound audio to a hidden <audio> element.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function attachAudio(pc: any): void {
  if (!pc || typeof document === 'undefined') return;
  let el = document.getElementById('merkaziya-remote-audio') as HTMLAudioElement | null;
  if (!el) {
    el = document.createElement('audio');
    el.id = 'merkaziya-remote-audio';
    el.autoplay = true;
    document.body.appendChild(el);
  }
  // Route playback to the agent's chosen speaker + volume, if any.
  applySpeaker(useAudioDevices.getState().speakerId);
  applySpeakerVolume(useAudioDevices.getState().speakerVolume);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  pc.addEventListener('track', (evt: any) => {
    if (evt.streams && evt.streams[0]) {
      el!.srcObject = evt.streams[0];
    } else if (evt.track) {
      const ms = new MediaStream();
      ms.addTrack(evt.track);
      el!.srcObject = ms;
    }
    el!.play().catch(() => { /* autoplay may be blocked until user gesture */ });
  });
}

export const useSipStore = create<SipStore>((set, get) => ({
  state: 'idle',
  registered: false,
  noLine: false,
  incoming: null,
  active: null,
  error: null,
  micError: null,
  creds: null,
  ua: null,
  session: null,
  recordCalls: loadRecordPref(),
  reconnectNonce: 0,
  otherTab: false,
  otherDevice: null,

  setRecordCalls() {
    // Recording is policy-enforced ON; the setter is a no-op kept for API
    // compatibility. The softphone never exposes an off-switch.
    set({ recordCalls: true });
  },

  async register(creds) {
    // 🔑 לעצור UA קיים לפני יצירת חדש. בלי זה כל קריאה חוזרת (רימאונט,
    // החלפת סטטוס, retry) ערמה UA נוסף שה-store איבד אליו הפניה — וכל
    // אחד המשיך לנסות להתחבר לנצח עם הלולאה הפנימית של JsSIP. ב-17/08
    // הערימה הזו (משתי מכונות) ירתה ~5 בקשות/שנייה על /ws, מילאה 98
    // taskprocessors באסטריסק והפילה את ARI — חיוג יוצא וסופטפון מתו.
    const prev = get().ua;
    if (prev && typeof prev.stop === 'function') {
      try { prev.stop(); } catch { /* כבר עצור */ }
    }
    set({ state: 'registering', creds: creds ?? null, ua: null });

    // Explicit soft mode (dev/demo): fake "ready" so the UI flows with no SIP.
    if (SOFT_MODE) {
      await new Promise((r) => setTimeout(r, 50));
      set({ state: 'ready', registered: true, error: null, noLine: false });
      return;
    }

    // אין קרדנצ'לס בכלל = למשתמש אין שלוחה (מנהל/סופר בלי קו). זה מצב
    // מכוון שהשרת מחזיר — לא תקלה: שקט בקונסול ובלי state של שגיאה.
    if (!creds) {
      console.info('[SIP] no extension assigned — softphone in no-line mode');
      set({ state: 'idle', registered: false, error: null, noLine: true });
      return;
    }

    // Real mode but creds are incomplete → do NOT silently fake a connection
    // (that produces "dials but nothing happens"). Surface a clear error so the
    // agent/admin knows the SIP extension isn't provisioned.
    if (!creds.wsServer || !creds.uri) {
      const missing = [
        !creds.wsServer && 'ws_url',
        !creds.uri && 'sip_uri',
      ].filter(Boolean).join(', ');
      console.error('[SIP] register aborted — incomplete credentials:', missing, creds);
      set({ state: 'error', registered: false, error: `SIP credentials incomplete (${missing})` });
      return;
    }
    set({ noLine: false });

    const lockName = sipLockName(creds.uri);
    if (!holdsSipLock(lockName)) {
      const got = await acquireSipLock(lockName, { onLost: lineTakenElsewhere });
      if (!got) {
        set({ state: 'error', registered: false, otherTab: true, error: OTHER_TAB_MSG });
        parkUntilLineFree();
        return;
      }
    }
    set({ otherTab: false });

    // בין מכשירים: השרת מכריע. כניסה חדשה לוקחת בכוח; אחרת מכשיר אחר חי ⇒
    // לא נרשמים (אחרת פינג-פונג).
    const claim = await claimSipLine(!claimedThisPage);
    claimedThisPage = true;
    if (!claim.owner) {
      set({ state: 'error', registered: false, otherDevice: claim.other, error: otherDeviceMessage(claim.other) });
      pollUntilDeviceFree();
      return;
    }
    stopDevicePoll();
    set({ otherDevice: null });

    try {
      if (!JsSIP) {
        const mod = await import('jssip');
        JsSIP = mod.default ?? mod;
      }

      const domain = creds.domain ?? DEFAULT_DOMAIN;
      const socket = new JsSIP.WebSocketInterface(creds.wsServer);
      const ua = new JsSIP.UA({
        sockets: [socket],
        uri: creds.uri,
        password: creds.password,
        display_name: creds.displayName,
        register: true,
        register_expires: 600,
        session_timers: false,
        user_agent: 'Merkaziya/1.0',
        // backoff רגוע: ברירות המחדל של JsSIP (2s-30s) בשילוב UA-ים
        // נערמים ייצרו מטח בקשות. 5s-120s מספיק מהיר להתאוששות אמיתית
        // ואיטי מספיק כדי לא להפוך תקלת שרת למתקפה עצמית.
        connection_recovery_min_interval: 5,
        connection_recovery_max_interval: 120,
        pcConfig: {
          iceServers: creds.iceServers && creds.iceServers.length
            ? creds.iceServers
            : defaultIceServers(domain),
          iceTransportPolicy: 'all',
          bundlePolicy: 'max-bundle',
        },
      });

      ua.on('registered', () => {
        set({ state: 'ready', registered: true, error: null });
        startPresenceHeartbeat();
        startClaimRenewal();
      });
      ua.on('unregistered', () => {
        // Keep the presence beat running. The agent is still sitting at the
        // desk with the tab open — only the SIP line dropped, and JsSIP is
        // already retrying. Stopping the beat here used to hand the agent a
        // second failure on top of the first: the 90s server sweep marked them
        // offline, so even after the line recovered the next dial behaved as if
        // they were gone. Presence stops on explicit unregister() / offline.
        set({ registered: false });
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ua.on('registrationFailed', (e: any) =>
        set({ state: 'error', error: String(e?.cause ?? 'register failed') })
      );

      // מפסק ניסיונות: טאב שלא מצליח להתחבר לא ינסה לנצח. 8 כשלונות
      // רצופים (עם ה-backoff שמעל: ~4 דקות) ⇒ עוצרים את ה-UA לגמרי
      // ומציגים שגיאה. טאב רדום שנשכח מפסיק להעמיס על השרת; רענון
      // מתחיל מחדש. המונה מתאפס בכל חיבור מוצלח.
      let wsFailures = 0;
      ua.on('connected', () => { wsFailures = 0; });
      ua.on('disconnected', () => {
        wsFailures++;
        // ה-WebSocket נפל ⇒ אסטריסק מוחק את ה-contact מיד ("due to shutdown"),
        // אבל JsSIP לא פולט unregistered — `registered` נשאר true עד שה-
        // recovery שלו (עד 120ש) נרשם שוב. 28/09/2026, קמפיין 7: הקו היה
        // מת 14:35:05–14:37:21, שני סבבים יצאו "כי רשום" ושני לקוחות ענו
        // ונזרקו. מורידים את הדגל ⇒ useSipRegistration נרשם מחדש מיד והסבבים
        // מחכים לקו. באמצע שיחה לא נוגעים — רישום מחדש עוצר את ה-UA והשיחה.
        const inCall = ['on-call', 'on-hold', 'ringing-in', 'ringing-out'].includes(get().state);
        if (!inCall && get().registered) set({ registered: false });
        if (wsFailures >= 8) {
          try { ua.stop(); } catch { /* כבר עצור */ }
          stopPresenceHeartbeat();
          set({
            state: 'error', registered: false, ua: null,
            error: 'החיבור לשרת נכשל שוב ושוב — רענן את הדף כדי לנסות מחדש',
          });
        }
      });

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ua.on('newRTCSession', (data: any) => {
        const session = data.session;

        // שיחה נכנסת בזמן שיחה פעילה — נדחית מיד עם 486 Busy, לפני שנוגעים
        // בשום state: בלי הגנה הזו ה-INVITE השני דרס את מצביע השיחה החיה,
        // חטף את אלמנט האודיו המשותף (הלקוח הפסיק לשמוע), הפך את המצב
        // ל-ringing-in והרינגטון צפצף ישר לאוזן. חריג: רגלי בקרה של מנהל
        // (האזנה/לחישה/השתלטות) עם X-Auto-Answer ממשיכות לעבוד.
        if (data.originator === 'remote') {
          const busy = ['on-call', 'on-hold', 'ringing-out', 'ringing-in'].includes(get().state);
          const isControlLeg = (() => {
            try { return data.request?.getHeader?.('X-Auto-Answer') === 'yes'; }
            catch { return false; }
          })();
          if (busy && ! isControlLeg) {
            try { session.terminate({ status_code: 486, reason_phrase: 'Busy Here' }); } catch { /* כבר נסגר */ }
            return;
          }
        }

        activeJsSipSession = session;

        // ICE מהיר: לא ממתינים לסיום איסוף מלא (ראו ICE_READY).
        let iceTimer: ReturnType<typeof setTimeout> | null = null;
        let iceDone = false;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        session.on('icecandidate', (e: any) => {
          if (iceDone || typeof e?.ready !== 'function') return;
          const finish = () => {
            if (iceDone) return;
            iceDone = true;
            if (iceTimer) clearTimeout(iceTimer);
            try { e.ready(); } catch { /* JsSIP כבר המשיך */ }
          };
          const type = String(e?.candidate?.type ?? e?.candidate?.candidate ?? '');
          if (type.includes('relay')) { finish(); return; }
          if (type.includes('srflx')) {
            if (iceTimer) clearTimeout(iceTimer);
            iceTimer = setTimeout(finish, ICE_READY.afterSrflxMs);
            return;
          }
          if (!iceTimer) iceTimer = setTimeout(finish, ICE_READY.maxMs);
        });

        // Wire audio: peerconnection event (incoming) + polling (outgoing).
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        session.on('peerconnection', (e: any) => attachAudio(e.peerconnection));
        let polls = 0;
        const findPc = setInterval(() => {
          polls++;
          if (session.connection) {
            clearInterval(findPc);
            attachAudio(session.connection);
          } else if (polls > 50) {
            clearInterval(findPc);
          }
        }, 100);

        if (data.originator === 'remote') {
          const from = cleanCallerNumber(
            data.request?.from?.uri?.user ?? session.remote_identity?.uri?.toString()
          );
          const name = data.request?.from?.display_name;
          // Monitoring / control legs (listen/whisper/barge/takeover/conference
          // that the backend originates to a MANAGER) carry X-Auto-Answer so the
          // manager is dropped straight INTO the call instead of seeing a
          // ringing "incoming call" they have to accept.
          const autoAnswer = (() => {
            try { return data.request?.getHeader?.('X-Auto-Answer') === 'yes'; }
            catch { return false; }
          })();
          if (autoAnswer) {
            set({ state: 'ringing-in', session, incoming: { from, displayName: name } });
            // Answer on the next tick so the session is fully wired first.
            setTimeout(() => { void get().answer(); }, 0);
          } else {
            set({ incoming: { from, displayName: name }, state: 'ringing-in', session });
          }
        } else {
          set({ state: 'ringing-out', session });
        }

        const onAnswered = () => {
          const number = cleanCallerNumber(session.remote_identity?.uri?.toString())
            || get().active?.number || '';
          set({
            state: 'on-call',
            incoming: null,
            active: {
              number,
              direction: data.originator === 'remote' ? 'inbound' : 'outbound',
              startedAt: Date.now(),
              durationSec: 0,
              muted: false,
              onHold: false,
            },
          });
          startTick(set, get);
          // resolve the backend CDR id so conference/transfer/leave can enable
          resolveLiveCallId(number);
        };
        session.on('accepted', onAnswered);
        session.on('confirmed', onAnswered);

        // Safety net for the reverse desync: the session is alive but the UI
        // lost it (a missed 'accepted'/'confirmed', or any state write that
        // raced the call). Without this the agent sees no call, has no hangup
        // button, and the customer stays connected — the exact complaint.
        // We only ever ADD the call back; we never invent one.
        const reconcile = setInterval(() => {
          if (!activeJsSipSession || activeJsSipSession !== session) return;
          const st = get().state;
          const established = (() => {
            try { return session.isEstablished?.() === true; }
            catch { return false; }
          })();
          const uiThinksIdle = st === 'idle' || st === 'ready' || st === 'wrap-up';
          if (established && uiThinksIdle) {
            const number = cleanCallerNumber(session.remote_identity?.uri?.toString())
              || get().active?.number || '';
            console.warn('[SIP] recovered a live call the UI had lost', { state: st, number });
            set({
              state: 'on-call',
              incoming: null,
              session,
              active: {
                number,
                direction: data.originator === 'remote' ? 'inbound' : 'outbound',
                startedAt: get().active?.startedAt ?? Date.now(),
                durationSec: get().active?.durationSec ?? 0,
                muted: false,
                onHold: false,
              },
            });
            startTick(set, get);
          }
        }, 3000);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        session.on('ended', (e: any) => {
          clearInterval(reconcile);
          clearInterval(findPc);
          stopTick();
          disposeSuppressed();
          activeJsSipSession = null;
          const endedBy: 'local' | 'remote' = e?.originator === 'local' ? 'local' : 'remote';
          set((s) => ({
            state: 'wrap-up',
            session: null,
            active: s.active ? { ...s.active, endedBy: s.active.endedBy ?? endedBy } : s.active,
          }));
        });

        session.on('failed', () => {
          clearInterval(reconcile);
          clearInterval(findPc);
          stopTick();
          disposeSuppressed();
          activeJsSipSession = null;
          clearDialInput();
          set({ state: 'ready', active: null, incoming: null, session: null });
        });
      });

      ua.start();
      set({ ua });
    } catch (e) {
      set({ state: 'error', error: String((e as Error).message) });
    }
  },

  unregister() {
    // Tearing the line down while a call is up used to strand it: ua.stop()
    // does not reliably end an established session, and the state went to
    // 'idle' — so the UI showed no call at all while the audio kept flowing and
    // the agent had no hangup button. End the call first, always.
    if (activeJsSipSession) {
      try { activeJsSipSession.terminate?.(); } catch { /* already gone */ }
      activeJsSipSession = null;
    }
    disposeSuppressed();
    stopTick();

    const ua = get().ua;
    if (ua && typeof ua.stop === 'function') ua.stop();
    stopPresenceHeartbeat();
    stopClaimRenewal();
    set({ ua: null, registered: false, state: 'idle', active: null, incoming: null, session: null });
  },

  lineDisabled: false,

  /**
   * שינוי סטטוס נוכחות דרך ה-store: מעבר ל"מנותק" גם מנתק את קו ה-SIP
   * (un-REGISTER — Asterisk מפסיק לצלצל), וחזרה ממנו רושמת את הקו מחדש
   * עם ה-creds השמורים. שרת קודם — כשל בשרת לא משנה כלום מקומית.
   */
  async setPresence(status) {
    const { agentApi } = await import('@/shared/api/agentApi');
    await agentApi.setPresence(status);

    if (status === 'offline') {
      set({ lineDisabled: true });
      get().unregister();
      return;
    }

    if (get().lineDisabled) {
      set({ lineDisabled: false });
      const creds = get().creds;
      if (creds) {
        // חובה להעביר את ה-creds השמורים — register() בלי ארגומנט מאפס אותם.
        await get().register(creds);
      }
      // בלי creds שמורים — useSipRegistration ימשוך אותם וירשום מחדש.
    }
  },

  async takeOverLine() {
    const creds = get().creds;
    if (!creds?.uri || get().lineDisabled) return false;
    const got = await acquireSipLock(sipLockName(creds.uri), { steal: true, onLost: lineTakenElsewhere });
    if (!got) return false;
    // מעבירים גם בשרת — המכשיר האחר יגלה בחידוש הבא שלו ויירד מהקו.
    await claimSipLine(true);
    stopDevicePoll();
    set({ otherTab: false, otherDevice: null, state: 'registering', error: RECONNECTING_MSG });
    await get().register(creds);
    const ok = await waitForRegistered(get);
    if (ok) {
      set({ error: null });
    } else if (get().error === RECONNECTING_MSG) {
      set({ state: 'error', error: LINE_DOWN_MSG });
    }
    return ok;
  },

  async reconnect() {
    if (get().lineDisabled) return false;
    if (get().noLine) return false;
    if (get().registered) return true;
    // "התחבר מחדש" בלשונית שהקו לא אצלה = להעביר אותו לכאן.
    if (get().otherTab || get().otherDevice) return get().takeOverLine();

    const creds = get().creds;
    if (creds) {
      // register() עוצר UA קודם ופותח חדש — זה בדיוק מה שתוקע רישום שנכשל
      // (registrationFailed / 8 ניתוקים) מחוץ ללולאת ה-retry של JsSIP.
      set({ error: RECONNECTING_MSG });
      await get().register(creds);
    } else {
      // בלי creds אין מה לרשום — מבקשים מ-useSipRegistration למשוך אותם.
      set({ state: 'registering', error: RECONNECTING_MSG, reconnectNonce: get().reconnectNonce + 1 });
    }

    const ok = await waitForRegistered(get);
    if (ok) {
      set({ error: null });
    } else if (get().error === RECONNECTING_MSG) {
      set({ state: 'error', error: LINE_DOWN_MSG });
    }
    return ok;
  },

  async refreshLine() {
    if (get().lineDisabled) return false;
    if (get().registered) return true;
    // סבב/חיוג מפורש מהלשונית הזו — הקו עובר לכאן, לאן שהנציג עובד.
    if (get().otherTab || get().otherDevice) return get().takeOverLine();

    // מאפסים creds ו-noLine ומבקשים מ-useSipRegistration למשוך מחדש: שלוחה
    // שנוצרה עכשיו (הצטרפות לקמפיין) אינה ב-creds השמורים, ומשתמש שהיה
    // בלי שלוחה נשאר במצב noLine עד רענון הדף.
    set({ noLine: false, creds: null, state: 'registering', error: RECONNECTING_MSG, reconnectNonce: get().reconnectNonce + 1 });

    const ok = await waitForRegistered(get);
    if (ok) {
      set({ error: null });
    } else if (get().error === RECONNECTING_MSG) {
      set({ state: 'error', error: LINE_DOWN_MSG });
    }
    return ok;
  },

  async dial(number, callerId, afterReconnect = false) {
    // מספר פסול נשלח עד עכשיו כ-INVITE ונכשל בשקט אצל הספק — המשתמש ראה
    // חיוג שלא קורה כלום איתו. עוצרים כאן עם הודעה במקום.
    const invalid = invalidNumberReason(number);
    if (invalid) {
      set({ state: 'error', error: invalid });
      return;
    }

    // כשל מיקרופון ידוע חוסם חיוג עד שהמשתמש מאשר את החלונית. בלי החסימה
    // כל לחיצה נוספת רק מייצרת "מחייג" מדומה נוסף.
    if (get().micError) return;

    const ua = get().ua;
    const creds = get().creds;
    const domain = creds?.domain ?? DEFAULT_DOMAIN;
    const cid = callerId ?? creds?.defaultCallerDid ?? undefined;

    if (ua && get().registered) {
      // REAL: place a WebRTC INVITE. Let the session events drive state so the
      // UI reflects the true call lifecycle (no fake timer).
      const extraHeaders: string[] = [];
      if (cid) extraHeaders.push(`X-Caller-ID: ${cid}`);
      // Tell the dialplan to start MixMonitor (same mechanism as the dialer).
      if (get().recordCalls) extraHeaders.push('X-Record-Call: yes');
      try {
        // Hand JsSIP a pre-built (optionally denoised) mic stream. When a
        // mediaStream is supplied JsSIP skips its own getUserMedia and sends
        // exactly these tracks — that's how RNNoise gets onto the RTP path.
        const mediaStream = await buildOutboundStream();
        ua.call(buildTargetUri(number, domain), {
          mediaStream,
          mediaConstraints: { audio: true, video: false },
          rtcOfferConstraints: { offerToReceiveAudio: true, offerToReceiveVideo: false },
          extraHeaders,
        });
      } catch (e) {
        // כשל בתפיסת המיקרופון (או ב-UA) נבלע כאן בשקט, וה-state נקבע
        // ל-'ringing-out' מחוץ ל-try — כך שהמסך הציג "מחייג" עם טיימר קפוא
        // על 0:00 בזמן שאף INVITE לא נשלח. אומת בפרודקשן: אפס תעבורה
        // מהשלוחה בזמן הלחיצה. מדווחים במפורש ונשארים ב-idle.
        disposeSuppressed();
        const err = e as { name?: string; message?: string };
        console.error('[SIP] dial aborted — mic/UA failure:', err?.name, err?.message);
        set({ state: 'idle', active: null, micError: micErrorReason(err) });
        return;
      }
      set({
        state: 'ringing-out',
        active: {
          number, direction: 'outbound', startedAt: Date.now(),
          durationSec: 0, muted: false, onHold: false,
        },
      });
      return;
    }

    // Only the explicit demo/soft mode may fake a call. In real mode, dialing
    // while not registered must fail loudly — never pretend to ring.
    if (SOFT_MODE) {
      set({
        state: 'ringing-out',
        active: {
          number, direction: 'outbound', startedAt: Date.now(),
          durationSec: 0, muted: false, onHold: false,
        },
      });
      startTick(set, get);
      return;
    }

    // למשתמש אין שלוחה בכלל — מצב מכוון של השרת, לא תקלה: מסבירים למי
    // שלחץ "חייג" מה חסר, בלי שגיאה בקונסול ובלי נקודה אדומה.
    if (get().noLine) {
      console.info('[SIP] dial refused — no extension assigned to this user.');
      set({ state: 'idle', error: NO_LINE_MSG });
      return;
    }

    // הקו נפל (או מעולם לא נרשם) והנציג לחץ "חייג": במקום להיכשל מיד,
    // מנסים פעם אחת להתחבר מחדש ואז מחייגים. זה מה שהיה מציג "לא רשום" גם
    // כשהרשת חזרה מזמן — רישום שנכשל פעם אחת לא ניסה שוב עד רענון הדף.
    if (!afterReconnect) {
      console.warn('[SIP] not registered on dial — reconnecting before the INVITE.');
      const ok = await get().reconnect();
      if (ok) return get().dial(number, callerId, true);
      return;
    }

    console.error('[SIP] dial aborted — softphone not registered (no SIP line).');
    set({ state: 'error', error: LINE_DOWN_MSG });
  },

  async answer() {
    const incoming = get().incoming;
    if (!incoming) return;

    try {
      const mediaStream = await buildOutboundStream();
      activeJsSipSession?.answer?.({
        mediaStream,
        mediaConstraints: { audio: true, video: false },
      });
    } catch {
      // Fall back to letting JsSIP grab the mic itself rather than failing the
      // answer outright.
      disposeSuppressed();
      activeJsSipSession?.answer?.({
        mediaConstraints: { audio: micConstraint(), video: false },
      });
    }

    set({
      state: 'on-call',
      incoming: null,
      active: {
        number: incoming.from,
        direction: 'inbound',
        startedAt: Date.now(),
        durationSec: 0,
        muted: false,
        onHold: false,
      },
    });
    startTick(set, get);
    // resolve the backend CDR id so conference/transfer/leave can enable
    resolveLiveCallId(incoming.from);
  },

  reject() {
    // 486 מפורש — "עסוק", לא 480 ברירת המחדל של JsSIP (חשוב לניתוב חוזר בצד המרכזיה).
    try { activeJsSipSession?.terminate?.({ status_code: 486, reason_phrase: 'Busy Here' }); } catch { activeJsSipSession?.terminate?.(); }
    activeJsSipSession = null;
    disposeSuppressed();
    set({ incoming: null, state: 'ready' });
  },

  hangup() {
    activeJsSipSession?.terminate?.();
    activeJsSipSession = null;
    disposeSuppressed();
    stopTick();
    if (get().active) {
      set((s) => ({ state: 'wrap-up', active: s.active ? { ...s.active, endedBy: 'local' } : s.active }));
    } else {
      clearDialInput();
      set({ state: 'ready', active: null, incoming: null });
    }
  },

  hold() {
    activeJsSipSession?.hold?.();
    set((s) => (s.active ? { active: { ...s.active, onHold: true }, state: 'on-hold' } : s));
  },

  unhold() {
    activeJsSipSession?.unhold?.();
    set((s) => (s.active ? { active: { ...s.active, onHold: false }, state: 'on-call' } : s));
  },

  mute(next) {
    const newMuted = next ?? !get().active?.muted;
    if (newMuted) {
      activeJsSipSession?.mute?.({ audio: true });
    } else {
      activeJsSipSession?.unmute?.({ audio: true });
    }
    set((s) => (s.active ? { active: { ...s.active, muted: newMuted } } : s));
  },

  sendDtmf(digit) {
    activeJsSipSession?.sendDTMF?.(digit);
  },

  enterWrapup() {
    set({ state: 'wrap-up' });
  },

  finishWrapup() {
    // "שמור וסיים" באמצע שיחה: הסיכום נשמר, אבל השיחה עדיין חיה. איפוס
    // ל-ready מחק אותה מה-UI ("[SIP] recovered a live call the UI had lost",
    // 28/09/2026) — ה"נתק" נעלם עד ש-reconcile החזיר אותה.
    if (['on-call', 'on-hold', 'ringing-in', 'ringing-out'].includes(get().state)) return;
    // בסיום שיחה החייגן מתנקה (בקשת דורון) — המספר נשאר גלוי לאורך השיחה
    // וסיכום ה-wrap-up, ומתאפס רק בחזרה למצב מוכן.
    clearDialInput();
    set({ state: 'ready', active: null });
  },

  clearMicError() {
    set({ micError: null });
  },
}));

/** ניקוי שדה החיוג בסיום שיחה — import עצל כדי לא ליצור תלות מעגלית. */
function clearDialInput(): void {
  void import('./dialInput')
    .then((m) => m.useDialInput.getState().clear())
    .catch(() => {});
}

function startTick(set: (patch: Partial<SipStore>) => void, get: () => SipStore) {
  stopTick();
  tickHandle = (typeof window !== 'undefined' ? window : globalThis).setInterval(() => {
    const active = get().active;
    if (!active) {
      stopTick();
      return;
    }
    set({
      active: {
        ...active,
        durationSec: Math.floor((Date.now() - active.startedAt) / 1000),
      },
    });
  }, 1000) as unknown as number;
}

function stopTick() {
  if (tickHandle !== undefined) {
    (typeof window !== 'undefined' ? window : globalThis).clearInterval(tickHandle);
    tickHandle = undefined;
  }
}

// The softphone dials over WebRTC and never learns its backend CDR id. The call
// row is created a beat later by the internal/call-start webhook, so we poll the
// agent's live call briefly after connect and stamp the id onto `active` — that's
// what enables conference/transfer/leave. Best-effort: if it never resolves the
// controls stay disabled (no worse than before). agentApi is imported lazily to
// keep this store free of a hard API dependency + avoid a circular import.
let liveCallResolveToken = 0;
function resolveLiveCallId(number: string): void {
  const token = ++liveCallResolveToken; // invalidates any prior in-flight resolver
  let attempts = 0;
  const tryOnce = async () => {
    // stop if this resolver was superseded or the call is gone
    if (token !== liveCallResolveToken) return;
    const active = useSipStore.getState().active;
    if (!active || active.callId != null) return;

    try {
      const { agentApi } = await import('@/shared/api/agentApi');
      const live = await agentApi.liveCall(number);
      if (token !== liveCallResolveToken) return;
      const cur = useSipStore.getState().active;
      if (cur && live?.call_id != null) {
        useSipStore.setState({ active: { ...cur, callId: live.call_id } });
        return;
      }
    } catch {
      // ignore — retry below
    }

    if (++attempts < 6) {
      (typeof window !== 'undefined' ? window : globalThis).setTimeout(tryOnce, 1500);
    }
  };
  // first attempt after a short delay — give the webhook time to create the row
  (typeof window !== 'undefined' ? window : globalThis).setTimeout(tryOnce, 1200);
}

// For dev/demo/tests: simulate an incoming call without a real INVITE.
export function simulateIncoming(from: string, displayName?: string): void {
  useSipStore.setState({ incoming: { from, displayName }, state: 'ringing-in' });
}
